import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// Types
interface UseSignupScreen1Props {
  domainValidation: boolean;
  fandfUser: any;
  clientId: string | undefined;
  handleResponseSSO: (response: any) => void;
  isStandardAuthActive: boolean;
  isMicrosoftAuthActive: boolean;
  isGoogleAuthActive: boolean;
  singleSignOn: any[];
  handleContinue: (dataObj: any) => void;
  error: string | undefined;
}

interface UseSignupScreen1Return {
  // Form state
  register: any;
  handleSubmit: any;
  formState: any;
  watch: any;
  clearErrors: any;

  // UI state
  isCompLoading: boolean;
  setCompLoading: (loading: boolean) => void;
  errorMessage: string | undefined;
  setErrorMessage: (message: string | undefined) => void;
  showPassword: boolean;
  setShowPassword: (show: boolean) => void;
  showConfirmPassword: boolean;
  setShowConfirmPassword: (show: boolean) => void;
  submitting: boolean;
  setSubmitting: (submitting: boolean) => void;

  // Handlers
  handleContinue: (data: any) => void;
  handleShowPasswordClick: () => void;
  handleShowConfirmPasswordClick: () => void;
  handleFieldChange: (fieldName: string) => void;

  // Computed state
  isFormLoading: boolean;

  // Refs for focus management
  emailInputRef: React.RefObject<HTMLInputElement>;
  passwordInputRef: React.RefObject<HTMLInputElement>;
  confirmPasswordInputRef: React.RefObject<HTMLInputElement>;
}

export const useSignupScreen1 = ({
  handleContinue: handleContinueProp,
  error,
}: UseSignupScreen1Props): UseSignupScreen1Return => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    watch,
  } = useForm();

  const [isCompLoading, setCompLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Refs for focus management
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null);

  // Hide passwords when submitting
  useEffect(() => {
    if (submitting) {
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [submitting]);

  // Handle external error from parent
  useEffect(() => {
    setErrorMessage(error);
    setSubmitting(false);
  }, [error]);

  const handleContinue = useCallback(
    (data: any) => {
      setSubmitting(true);
      const dataObj = {
        progress: 'screen1',
        email: data.email,
        password: data.password,
      };

      handleContinueProp(dataObj);
    },
    [handleContinueProp]
  );

  const handleShowPasswordClick = useCallback(() => {
    setShowPassword(!showPassword);
    clearErrors('password');
    setErrorMessage('');
  }, [showPassword, clearErrors]);

  const handleShowConfirmPasswordClick = useCallback(() => {
    setShowConfirmPassword(!showConfirmPassword);
    clearErrors('confirm_password');
    setErrorMessage('');
  }, [showConfirmPassword, clearErrors]);

  const handleFieldChange = useCallback(
    (fieldName: string) => {
      clearErrors(fieldName);
      setErrorMessage('');
    },
    [clearErrors]
  );

  const isFormLoading = isCompLoading || submitting;

  return {
    // Form state
    register,
    handleSubmit,
    formState: { errors },
    watch,
    clearErrors,

    // UI state
    isCompLoading,
    setCompLoading,
    errorMessage,
    setErrorMessage,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    submitting,
    setSubmitting,

    // Handlers
    handleContinue,
    handleShowPasswordClick,
    handleShowConfirmPasswordClick,
    handleFieldChange,

    // Computed state
    isFormLoading,

    // Refs
    emailInputRef,
    passwordInputRef,
    confirmPasswordInputRef,
  };
};
